import type { Manifest } from '../shared/types';
import {
  isString,
  isArray,
  isObject,
  isPlainObject,
  isNotBlank,
  isSHA1,
  isISODateString,
  isBaseURL,
  isRelativeFilePath,
  includes,
  endsWith,
  every,
  key,
} from '../utils/validators';

interface FieldSchema<Required extends boolean, T> {
  required: Required;
  // T is "non-nullable if present", not "non-nullable, full stop" — whether
  // the field can be missing entirely is `required` above, checked before any
  // validator runs (see validator.ts). T only describes the field's value
  // once we already know it's there.
  validators: Array<(value: T) => boolean>;
}

// Mapped directly over `keyof Manifest` — Manifest's `[key: string]: unknown`
// escape hatch is gone as of Phase 14 PR 3, so this now provably can't drift:
// adding, removing, or retyping a Manifest field forces a matching change
// here, since ManifestSchema no longer has an independent key list at all.
// `required`'s type is derived from Manifest[K] itself (true unless Manifest
// already marks the field optional), so schema.ts can't just be wrong about
// a field's required-ness either — tsc rejects `required: false` on a field
// Manifest declares non-optional, and vice versa.
type ManifestSchema = {
  [K in keyof Manifest]-?: FieldSchema<
    undefined extends Manifest[K] ? false : true,
    NonNullable<Manifest[K]>
  >;
};

const translationValidators: Array<
  (value: Record<string, unknown>) => boolean
> = [
  isObject,
  key('template', isString, isRelativeFilePath('json'), includes('__LANG__')),
  key('namespace', isString, isNotBlank),
];

const schema: ManifestSchema = {
  lastModified: {
    required: true,
    validators: [isString, isNotBlank],
  },
  exposed: {
    required: true,
    validators: [isString, isNotBlank],
  },
  createdAt: {
    required: true,
    validators: [isString, isISODateString],
  },
  gitRevision: {
    required: true,
    validators: [isString, isSHA1],
  },
  baseURL: {
    required: true,
    validators: [isString, isBaseURL, endsWith('/')],
  },
  script: {
    required: true,
    validators: [isString, isRelativeFilePath('js')],
  },
  style: {
    required: false,
    validators: [isString, isRelativeFilePath('css')],
  },
  translation: {
    required: false,
    validators: translationValidators,
  },
  translations: {
    required: false,
    validators: [isArray, every(...translationValidators)],
  },
  externalDependencies: {
    required: false,
    validators: [isArray, every(isString, isBaseURL, endsWith('.js', '.css'))],
  },
  additionalCreateOptions: {
    required: false,
    validators: [isPlainObject],
  },
};

export default schema;
